import { useForm } from "react-hook-form";
import Form from "../../interface/Form";
import FormRow from "../../interface/FormRow";
import Input from "../../interface/Input";
import { useEffect, useState } from "react";
import ButtonNeu from "../../interface/ButtonNeu";
import { useCheckUserExist } from "./useCheckUserExist";
import { useGetUserDetails } from "./useGetUserDetails";
import styled from "styled-components";
import toast from "react-hot-toast";
import { useUpdateUser } from "./UseAddUpdateUser";
import { TbPencilSearch } from "react-icons/tb";
import Modal from "../../interface/Modal";
import ImageEdit from "./ImageEdit";
import { createUserAndTriggerForgotPassword } from "../../services/apiAuth";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../services/firebase";
import { useAzureAuth } from "./entra/useAzureAuth";

const ImageContainer = styled.div`
  position: relative;
  align-items: center;
`;

const StyledImage = styled.img`
  width: 15rem;
  height: 15rem;
  border-radius: 50%;
  border: 1px solid var(--color-grey-100);
  box-shadow: var(--shadow-btn);
`;

const ImageButton = styled.button`
  position: absolute;
  bottom: -3px;
  left: 0;
  right: 0;
  margin: auto;
  width: fit-content;
  padding: 0.35rem;
  border-radius: 50%;
  background-color: var(--color-grey-50);
  border: 1px solid var(--color-grey-500);
  &:hover {
    background-color: var(--color-grey-500);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Define a mapping of departments to positions
const departmentPositions = {
  Management: ["Site Manager", "Maintenance Manager", "Stores Manager"],
  ProTechtives: ["Continuous Improvement", "Master Data"],
  "Shift 1": [
    "Shift Leader",
    "Shift Assistant",
    "Technician",
    "System Operator",
  ],
  "Shift 2": [
    "Shift Leader",
    "Shift Assistant",
    "Technician",
    "System Operator",
  ],
  "Shift 3": [
    "Shift Leader",
    "Shift Assistant",
    "Technician",
    "System Operator",
  ],
  // Add other departments and their respective positions as needed
};

function CreateEditUserForm() {
  const [enableEntraCheck, setEnableEntraCheck] = useState(true); // ✅ Toggle for Entra check
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [isNewlyCreated, setIsNewlyCreated] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userData, setUserData] = useState({});
  const [userImage, setUserImage] = useState(null);
  const { mutate: checkUser } = useCheckUserExist();
  const { mutate: getUserDetails } = useGetUserDetails();
  const { mutate: addUpdateUser } = useUpdateUser();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const { checkUserExistsInEntra } = useAzureAuth();

  const company = watch("company"); //to trigger wiToolResource hide if not Witron
  const department = watch("department");

  console.log(watch("department"), watch("position"));

  // Render positions based on the selected department
  const renderPositionOptions = () => {
    // Get positions for the selected department, or return an empty array if none
    const positions = departmentPositions[department] || [];
    // Map positions to option elements
    return positions.map((position) => (
      <option key={position} value={position}>
        {position}
      </option>
    ));
  };

  useEffect(() => {
    console.log("Set userData Effect in CreateUserForm triggered");
    if (isExistingUser && userData) {
      setValue("name", userData.userName || "");
      setValue("company", userData.company || "");
      setValue("department", userData.department || "");
      setValue("position", userData.position || "");
      setValue("skillSet", userData.skillSet || "");
      setValue("wiToolResource", userData.wiToolResource || "");
      setValue("accessLevel", userData.accessLevel === "Admin");
      setUserImage(userData.userImageBase64 || "");
    }
  }, [userData, isExistingUser, setValue]);

  useEffect(() => {
    // If department changes and there's a predefined position for the user, set it
    if (
      userData.position &&
      departmentPositions[department]?.includes(userData.position)
    ) {
      setValue("position", userData.position);
    } else {
      // This resets the position field if the department changes and there's no matching position
      setValue("position", "");
    }
  }, [department, userData.position, setValue]);

  const handlePasswordReset = async () => {
    if (userEmail) {
      try {
        // Assuming `sendPasswordResetEmail` is a function that calls Firebase's password reset feature
        await sendPasswordResetEmail(auth, userEmail);
        toast.success("Password reset email sent successfully.");
      } catch (error) {
        toast.error(`Failed to send password reset email: ${error.message}`);
      }
    } else {
      toast.error("No user email found for password reset.");
    }
  };

  const handleEmailBlur = async (e) => {
    const email = e.target.value.trim().toLowerCase();
    console.log("🔍 Checking email:", email);

    if (!email) {
      console.warn("⚠️ Email is empty. Skipping check.");
      return;
    }

    setUserEmail(email);

    if (email.endsWith("@rema.no")) {
      if (!enableEntraCheck) {
        console.log("🔹 Skipping both Entra & Firebase check for @rema.no");
        return;
      }

      console.log("🔹 Checking user existence in Entra (Azure AD)...");
      if (typeof checkUserExistsInEntra !== "function") {
        console.error("❌ checkUserExistsInEntra is not defined correctly.");
        return;
      }

      try {
        const existsInEntra = await checkUserExistsInEntra(email);
        console.log(`✅ Entra Check Result: ${existsInEntra}`);

        if (existsInEntra) {
          setIsExistingUser(true);
          getUserDetails(
            { email },
            {
              onSuccess: (response) => {
                if (response.userExists) {
                  setUserData(response.data.data);
                  toast.success("User data retrieved from backend.");
                } else {
                  setIsNewlyCreated(true);
                  toast.success("User exists in Entra, but no backend data.");
                }
              },
              onError: (error) => toast.error(`Backend fetch failed: ${error}`),
            }
          );
        } else {
          setIsExistingUser(false);
          toast.error("User does not exist in Entra (Azure AD).");
        }
      } catch (error) {
        console.error("Error checking user in Entra:", error);
        toast.error("Failed to validate user in Entra.");
      }
      return; // ✅ Exit here to prevent Firebase check for @rema.no
    }

    // ✅ Only check Firebase for non-@rema.no emails
    console.log("🔹 Checking user existence in Firebase...");
    checkUser(email, {
      onSuccess: (existsInFirebase) => {
        setIsExistingUser(existsInFirebase);
        if (existsInFirebase) {
          getUserDetails(
            { email },
            {
              onSuccess: (response) => {
                if (response.userExists) {
                  setUserData(response.data.data);
                  toast.success("✅ User found in Firebase.");
                } else {
                  setIsNewlyCreated(true);
                  toast.success(
                    "Email exists in Firebase, but no backend data."
                  );
                }
              },
              onError: (error) => {
                toast.error(error);
              },
            }
          );
        } else {
          toast.error("Email not registered. Ready to create new user.");
        }
      },
      onError: (error) => {
        toast.error(`Firebase check failed: ${error}`);
      },
    });
  };

  const handleEmailEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleEmailBlur(e);
    }
  };

  function handleUpdateAvatar(editedImage) {
    setUserImage(editedImage);
  }

  const onSubmit = async (data) => {
    console.log("Submitted user data", data);
    if (!isExistingUser) {
      try {
        await createUserAndTriggerForgotPassword(data.email);
        toast.success("New user created. Password reset email sent.");
        setIsExistingUser(true);
        setIsNewlyCreated(true);
      } catch (error) {
        toast.error(`Failed to create user: ${error.message}`);
        return;
      }
    } else {
      // Existing user update logic
      const userData = {
        Email: data.email,
        UserName: data.name,
        Company: data.company,
        Department: data.department,
        Position: data.position,
        skillSet: data.skillSet,
        wiToolResource: data.wiToolResource,
        AccessLevel: data.accessLevel ? "Admin" : "User",
        UserImageBase64: userImage,
      };
      addUpdateUser(userData, {
        onSuccess: () => {
          toast.success("User updated successfully");
        },
        onError: (error) => {
          toast.error(`Update failed: ${error}`);
        },
      });
    }
  };

  return (
    <Form type="modal" onSubmit={handleSubmit(onSubmit)}>
      <FormRow label="Enter Email">
        <Input
          type="email"
          id="email"
          disabled={isExistingUser}
          {...register("email", { required: true })}
          onBlur={handleEmailBlur}
          onKeyDown={handleEmailEnter}
        />
      </FormRow>

      {/* Other fields, e.g., name, company, etc. */}
      <FormRow label="Enter Name">
        <Input
          type="text"
          {...register("name")}
          disabled={!isExistingUser}
          defaultValue={userData.userName || ""}
        />
      </FormRow>
      <FormRow label="Employer Company" $style="withRadioButtons">
        <input
          type="radio"
          id="witron"
          name="company"
          value="Witron"
          disabled={!isExistingUser}
          {...register("company", {
            required: isExistingUser && "Company is required.",
          })}
        />
        <input
          type="radio"
          id="customer"
          name="company"
          value="Customer"
          disabled={!isExistingUser}
          {...register("company", {
            required: isExistingUser && "Company is required.",
          })}
        />
      </FormRow>
      <FormRow label="Enter Department">
        <select
          id="department"
          disabled={!isExistingUser}
          {...register("department", {
            // required: isExistingUser && "Department is required.",
          })}
          onChange={(e) => {
            const selectedValue = e.target.value;
            setValue("department", selectedValue, { shouldValidate: true });
            // No need to preventDefault or stopPropagation
          }}
          defaultValue={userData.department || ""}
        >
          <option value="">
            {userData.department ? userData.department : "Select Department"}
          </option>
          <option value="Management">Management</option>
          <option value="ProTechtives">ProTechtives</option>
          <option value="Shift 1">Shift 1</option>
          <option value="Shift 2">Shift 2</option>
          <option value="Shift 3">Shift 3</option>
        </select>
      </FormRow>
      <FormRow label="Enter Position">
        <select
          id="position"
          disabled={!isExistingUser}
          {...register("position", {
            //required: isExistingUser && "Position is required.",
          })}
          onChange={(e) => {
            const selectedValue = e.target.value;
            setValue("position", selectedValue, { shouldValidate: true });
            // No need to preventDefault or stopPropagation
          }}
          defaultValue={userData.position || ""}
        >
          <option value="">
            {userData.position ? userData.position : "Select Position"}
          </option>
          {renderPositionOptions()}
        </select>
      </FormRow>
      <FormRow label="Enter Skill Set">
        <select
          id="skillSet"
          disabled={!isExistingUser}
          {...register("skillSet")}
          onChange={(e) => {
            const selectedValue = e.target.value;
            setValue("skillSet", selectedValue, { shouldValidate: true });
            // No need to preventDefault or stopPropagation
          }}
          defaultValue={userData.skillSet || ""}
        >
          <option value="">
            {userData.skillSet ? userData.skillSet : "Select Skillset"}
          </option>
          <option value=""></option>
          <option value="Mechanical">Mechanical</option>
          <option value="Electrical">Electrical</option>
          <option value="Automation">Automation</option>
        </select>
      </FormRow>
      {company === "Witron" && (
        <FormRow label="WiTool Resource Name">
          <Input
            type="text"
            id="wiToolResource"
            disabled={!isExistingUser}
            {...register("wiToolResource", {
              required: isExistingUser,
            })}
          />
        </FormRow>
      )}
      <FormRow label="Access Level">
        <CheckboxContainer>
          <input
            type="checkbox"
            id="accessLevel"
            disabled={!isExistingUser}
            {...register("accessLevel")}
          />
          <label htmlFor="accessLevel">Admin</label>
        </CheckboxContainer>
      </FormRow>
      <FormRow>
        <ImageContainer>
          <StyledImage
            src={userImage || "/images/DefaultUser.png"}
            alt="User Image"
          />
          <Modal>
            <Modal.Open opens="editImage">
              <ImageButton type="button">
                <TbPencilSearch />
              </ImageButton>
            </Modal.Open>
            <Modal.Window name="editImage">
              <ImageEdit onImageUpdate={handleUpdateAvatar} />
            </Modal.Window>
          </Modal>
        </ImageContainer>
      </FormRow>
      {/* ... other fields ... */}
      <ButtonNeu type="submit">
        {isExistingUser ? "Update User" : "Create User"}
      </ButtonNeu>
      {isExistingUser && (
        <ButtonNeu type="button" onClick={handlePasswordReset}>
          Reset Password
        </ButtonNeu>
      )}
    </Form>
  );
}

export default CreateEditUserForm;
